import { useForm } from '@tanstack/react-form';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { Button } from '../../../../components/ui/button';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { RadioGroup } from '../../../../components/ui/radio-group';
import { RadioCard } from '../../../../components/ui/radio-card';
import { PUBLIC_ACCESS_LEVELS } from '../properties/dialogs/PublicAccessDialog';
import { toast } from '../../../../components/ui/toaster';
import { validateAzureContainerName } from '../../../../core/engines/azureStorageNames';
import { cancelStorageRequest, newStorageRequestId } from '../services/azureStorageApi';
import { createStorageContainer } from './storageMutations';
import { StorageAccountName } from '../components/StorageAccountName';
import { useStorageReadOnly } from '../hooks/useStorageReadOnly';

type Target = { connectionId: string; serviceFolderId: string; refresh: () => void };
type DialogState = { target: Target | null; open: (target: Target) => void; close: () => void };

const useDialogStore = create<DialogState>((set) => ({
  target: null,
  open: (target) => set({ target }),
  close: () => set({ target: null }),
}));

export const openStorageCreateContainerDialog = (target: Target) => useDialogStore.getState().open(target);

const trackCreate = (outcome: 'success' | 'error' | 'cancelled', publicAccess: string) => {
  void window.electronAPI?.analytics?.track('feature:storage_create_container', { outcome, publicAccess }).catch(() => undefined);
};
const categoryOf = (error: unknown): string | undefined =>
  (error && typeof error === 'object' && 'category' in error ? String((error as { category: unknown }).category) : undefined);

/**
 * The form OWNS its create request (review C C11): the request id is minted here, Cancel /
 * Escape / close / unmount while it is pending sends `azure-storage:cancel` for exactly that id
 * (main stops it before dispatch), and a write the cancel could not stop — acknowledged, or
 * `ambiguous_outcome` because it was already sent — is still reported and refreshed.
 */
function ContainerForm({ target }: { target: Target }) {
  const close = useDialogStore((state) => state.close);
  // A read-only connection opens the dialog with Create unavailable and its reason, like Create table / Create queue.
  const { readOnly, reason: readOnlyReason } = useStorageReadOnly(target.connectionId);
  const pending = useRef<{ requestId: string; publicAccess: string } | null>(null);
  const abandoned = useRef(false);
  const cancelPending = (): void => {
    const request = pending.current;
    if (!request || abandoned.current) return;
    abandoned.current = true;
    trackCreate('cancelled', request.publicAccess);
    void cancelStorageRequest(request.requestId);
  };
  const dismiss = (): void => { cancelPending(); close(); };
  // Unmount while pending (tab/app teardown, the dialog store reset) cancels too.
  useEffect(() => () => cancelPending(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const form = useForm({
    defaultValues: { name: '', publicAccess: 'none' as 'none' | 'blob' | 'container', serverError: '' },
    onSubmit: async ({ value }) => {
      if (readOnly) return;
      const requestId = newStorageRequestId();
      pending.current = { requestId, publicAccess: value.publicAccess };
      abandoned.current = false;
      try {
        await createStorageContainer({ connectionId: target.connectionId, name: value.name, publicAccess: value.publicAccess, requestId });
        target.refresh();
        if (abandoned.current) {
          toast.warning({ title: 'Container created', description: `The cancel arrived after ${value.name} was created.` });
          return;
        }
        trackCreate('success', value.publicAccess);
        close();
      } catch (error) {
        const category = categoryOf(error);
        if (abandoned.current) {
          if (category === 'ambiguous_outcome') {
            toast.warning({ title: 'Container state unknown', description: `The create request for ${value.name} had already been sent. Refresh to check whether the container exists.` });
            target.refresh();
          }
          return;
        }
        const message = error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Container could not be created.';
        form.setFieldValue('serverError', message);
        trackCreate('error', value.publicAccess);
        target.refresh();
      } finally {
        if (pending.current?.requestId === requestId) pending.current = null;
      }
    },
  });
  return <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
    <DialogContent data-testid="storage-create-container-dialog">
      <DialogHeader>
        <DialogTitle>Create blob container</DialogTitle>
        <DialogDescription>Create a container in storage account <StorageAccountName connectionId={target.connectionId} />.</DialogDescription>
      </DialogHeader>
      <form onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit(); }} className="space-y-4">
        <form.Field name="name" validators={{
          onChange: ({ value }) => {
            const result = validateAzureContainerName(value);
            return result.valid ? undefined : result.reason;
          },
          onSubmit: ({ value }) => {
            const result = validateAzureContainerName(value);
            return result.valid ? undefined : result.reason;
          },
        }}>
          {(field) => <div className="space-y-2">
            <Label htmlFor="storage-container-name">Container name</Label>
            <Input spellCheck={false} autoComplete="off" id="storage-container-name" value={field.state.value} onBlur={field.handleBlur} onChange={(event) => field.handleChange(event.target.value)} autoFocus data-testid="storage-container-name" />
            {field.state.meta.errors[0] && <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>}
          </div>}
        </form.Field>
        {/* C1 (N27): the same cards with meanings as Set public access level (it was a bare Select here). */}
        <form.Field name="publicAccess">{(field) => <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Public access</legend>
          <RadioGroup value={field.state.value} onValueChange={(value) => field.handleChange(value as 'none' | 'blob' | 'container')} aria-label="Public access" data-testid="storage-container-public-access">
            {PUBLIC_ACCESS_LEVELS.map((option) => <RadioCard key={option.value} value={option.value} label={option.label} checked={field.state.value === option.value} detail={option.detail} testId={`storage-container-public-access-${option.value}`} />)}
          </RadioGroup>
        </fieldset>}</form.Field>
        <form.Subscribe selector={(state) => state.values.serverError}>{(error) => error ? <p className="text-sm text-destructive" data-testid="storage-create-container-error">{error}</p> : null}</form.Subscribe>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={dismiss}>Cancel</Button>
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>{([canSubmit, isSubmitting]) => <ReasonButton type="submit" reason={readOnlyReason} disabled={!canSubmit || isSubmitting} data-testid="storage-create-container-submit">Create</ReasonButton>}</form.Subscribe>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
}

export function StorageCreateContainerDialog() {
  const target = useDialogStore((state) => state.target);
  return target ? <ContainerForm target={target} /> : null;
}
